import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { AxiosResponse } from "axios";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import { FaPlus } from "react-icons/fa";
import * as Yup from "yup";
import { useFormik } from "formik";

import $axios from "../../api/accessor";
import { $api } from "../../api/api";
import { getCookie } from "../../utils/utils";

import Spinner from "../../components/shared/Spinner/Spinner";
import MainButton from "../../components/shared/MainButton/MainButton";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import ShowImage from "../../components/shared/show-image/ShowImage";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { LanguageService } from "../../services/languages.service";
import { languagesDataType } from "../../interfaces/admin.interface";

const token = getCookie("BscAdminToken");

// DƏYİŞİKLİK: Default dil kiçik hərflə
const DEFAULT_LANG = "uz";

// --- İnterfeyslər ---

interface InstaGalleryTranslationResponse {
  id?: number;
  instagram_gallery_id?: number;
  locale: string;
  img?: string;
  // title silindi
  created_at?: string;
  updated_at?: string;
}

interface InstaGalleryDataType {
  id: number;
  url: string;
  created_at: string;
  updated_at: string;
  translations?: InstaGalleryTranslationResponse[];
  key?: number;
}

type FormTranslation = {
  lang_code: string;
  img?: string;
  // title silindi
};

type FormValues = {
  img: string;
  url: string;
  translations: FormTranslation[];
};

// Düzəliş: Payload strukturu (title silindi, key-lər string)
type Payload = {
  url: string;
  translations: {
    [key: string]: {
      img: string;
    };
  };
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const InstagramGallery = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [instaGalleryData, setInstaGalleryData] = useState<
    InstaGalleryDataType[]
  >([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] =
    useState<InstaGalleryDataType | null>(null);

  // Hər dil üçün ayrı fayl siyahısı (key-lər kiçik hərflə olacaq: 'uz', 'en')
  const [fileLists, setFileLists] = useState<{ [key: string]: any[] }>({});

  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState(DEFAULT_LANG);

  const mapResponseTranslationsToFormik = (
    trs: InstaGalleryTranslationResponse[],
  ): FormTranslation[] =>
    (trs || []).map((t) => ({
      lang_code: t.locale.toLowerCase(), // Kiçik hərflə
      img: t.img || "",
    }));

  const getTranslationForTable = (record: InstaGalleryDataType) => {
    const trs = record.translations || [];
    return (
      trs.find((t) => t.locale.toLowerCase() === DEFAULT_LANG) || trs[0] || null
    );
  };

  const fetchInstaGallery = async () => {
    setLoading(true);
    try {
      const res: AxiosResponse = await $axios.get($api("insta_gallery"));
      const list: InstaGalleryDataType[] = (res.data?.data || []).map(
        (item: InstaGalleryDataType, index: number) => ({
          ...item,
          key: item.id ?? index + 1,
        }),
      );
      setInstaGalleryData(list);
    } catch (error) {
      console.log(error);
      message.error("Instagram qalereyasını yükləmək alınmadı.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllLanguages = async () => {
    try {
      const res = await LanguageService.getAllLanguages();
      if (res.data) setLangs(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    setLoading(true);
    setLangCode(DEFAULT_LANG);
    Promise.all([fetchAllLanguages(), fetchInstaGallery()]).finally(() =>
      setLoading(false),
    );
  }, []);

  const handleAfterSubmit = async (resetFormFn: () => void) => {
    setLoading(true);
    setIsModalVisible(false);
    resetFormFn();
    setFileLists({});
    await fetchInstaGallery();
    setLoading(false);
  };

  const { handleSubmit, values, resetForm, setFieldValue, touched, errors } =
    useFormik<FormValues>({
      initialValues: {
        img: "",
        url: "",
        translations: [],
      },
      validationSchema: Yup.object({
        url: Yup.string()
          .required("Instagram linki tələb olunur")
          .url("Düzgün URL daxil edin"),
      }),
      onSubmit: async (vals) => {
        setLoading(true);
        try {
          const translationsObj: any = {};

          // Helper: Şəkli tapmaq
          const getImageForLang = async (code: string) => {
            const currentFileList = fileLists[code];
            // 1. Yeni fayl
            if (currentFileList && currentFileList[0]?.originFileObj) {
              return await fileToBase64(currentFileList[0].originFileObj);
            }
            // 2. Mövcud URL (Edit)
            if (currentFileList && currentFileList[0]?.url) {
              return currentFileList[0].url;
            }
            // 3. Formik values (backup)
            const tr = vals.translations.find((t) => t.lang_code === code);
            if (tr && tr.img) return tr.img;

            return "";
          };

          // Dillər üzrə dövr (kiçik hərflərlə)
          for (const lang of langs) {
            const code = lang.code.toLowerCase(); // 'uz', 'en' ...

            // Şəkli əldə et
            const finalImg = await getImageForLang(code);

            // Default dildirsə və ya şəkil varsa payload-a əlavə et
            if (code === DEFAULT_LANG || finalImg) {
              translationsObj[code] = {
                img: finalImg,
              };
            }
          }

          const payload: Payload = {
            url: vals.url,
            translations: translationsObj,
          };

          let response;
          if (isEditing && editingRecord) {
            response = await $axios.put(
              $api("insta_gallery_id", { id: editingRecord.id }),
              payload,
              { headers: { Authorization: `Bearer ${token}` } },
            );
          } else {
            response = await $axios.post($api("insta_gallery"), payload, {
              headers: { Authorization: `Bearer ${token}` },
            });
          }

          if (response.status === 200 || response.status === 201) {
            message.success(
              isEditing ? "Post yeniləndi!" : "Post əlavə edildi!",
            );
            await handleAfterSubmit(resetForm);
          }
        } catch (error: any) {
          console.log(error);
          message.error(error.response?.data?.message || "Xəta baş verdi.");
          setLoading(false);
        }
      },
    });

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode(DEFAULT_LANG);
    setFileLists({});
  };

  const handleEditClick = (record: InstaGalleryDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);

    const trs = record.translations || [];

    setFieldValue("url", record.url || "");

    const mapped = mapResponseTranslationsToFormik(trs);
    setFieldValue("translations", mapped);

    // Mövcud şəkilləri fileLists obyektinə doldururuq
    const initialFileLists: { [key: string]: any[] } = {};

    mapped.forEach((t) => {
      // API-dən gələn lang_code-u kiçik hərflə saxlayırıq
      const code = t.lang_code.toLowerCase();
      if (t.img) {
        initialFileLists[code] = [
          {
            uid: `-1-${code}`,
            name: "image",
            status: "done",
            url: t.img,
          },
        ];
      }
    });

    setFileLists(initialFileLists);

    // Vizual olaraq inputun dolu görünməsi üçün
    if (initialFileLists[DEFAULT_LANG]) {
      setFieldValue("img", "image_selected");
    } else {
      setFieldValue("img", "");
    }

    setLangCode(DEFAULT_LANG);
  };

  const handleDeleteClick = async (record: InstaGalleryDataType) => {
    try {
      setLoading(true);
      const res = await $axios.delete(
        $api("insta_gallery_id", { id: record.id }),
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.status === 200) {
        message.success("Post uğurla silindi!");
        await fetchInstaGallery();
      }
    } catch (error) {
      message.error("Postu silərkən xəta baş verdi.");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentFileList = () => {
    return fileLists[langCode] || [];
  };

  const handleFileChange = (files: any) => {
    let newFileList: any[] = [];
    if (typeof files === "function")
      newFileList = files(fileLists[langCode] || []);
    else if (Array.isArray(files)) newFileList = files;

    const finalFiles = newFileList.slice(-1);

    setFileLists((prev) => ({
      ...prev,
      [langCode]: finalFiles,
    }));

    if (finalFiles.length > 0) {
      setFieldValue("img", "image_present");
    }
  };

  const columns: TableProps<InstaGalleryDataType>["columns"] = useMemo(
    () => [
      {
        title: "№",
        dataIndex: "key",
        sorter: (a, b) => (a.key ?? 0) - (b.key ?? 0),
      },
      {
        title: "Şəkil",
        dataIndex: "img",
        render: (_: any, record) => {
          const tr = getTranslationForTable(record);
          const src = tr?.img || "";
          return (
            <div
              style={{ cursor: src ? "pointer" : "default" }}
              onClick={() => {
                if (src) {
                  setSelectedImage(src);
                  setShowImageModal(true);
                }
              }}
            >
              {src ? (
                <img
                  style={{
                    width: 40,
                    height: 40,
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                  src={src}
                  alt={`insta-${record.id}`}
                />
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        title: "Instagram URL",
        dataIndex: "url",
        render: (url: string) =>
          url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#03a9f4" }}
            >
              Link
            </a>
          ) : (
            "-"
          ),
      },
      {
        title: "Tarix",
        dataIndex: "created_at",
        render: (date: string) =>
          date
            ? new Date(date).toLocaleString("az-AZ", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
            : "-",
      },
      {
        title: (
          <div className="settings">
            <i className="fa-solid fa-gear"></i>
          </div>
        ),
        key: "action",
        width: 100,
        render: (_: any, record) => (
          <div className="settings_icons">
            <div className="edit" onClick={() => handleEditClick(record)}>
              <i className="fa-solid fa-pen-to-square"></i>
            </div>
            <Popconfirm
              title="Silinsin?"
              onConfirm={() => handleDeleteClick(record)}
              okText="Bəli"
              cancelText="Xeyr"
              placement="topRight"
            >
              <div className="delete">
                <i className="fa-solid fa-trash"></i>
              </div>
            </Popconfirm>
          </div>
        ),
      },
    ],
    [langCode],
  );

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Mebel Qalereyası</title>
      </Helmet>

      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Mebel Qalereyası</h3>
          </div>

          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>

            <div className="pages_table">
              <Table<InstaGalleryDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={instaGalleryData}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Postu redaktə et" : "Yeni post əlavə et"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          resetForm();
          setFileLists({});
          setLangCode(DEFAULT_LANG);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={(c: string) => setLangCode(c)}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />

          <div className="form_input">
            {/* Title input silindi, yalnız Şəkil və URL qaldı */}
            <label className="label">Şəkil ({langCode})</label>
            <SelectImage
              image_name={`Şəkil seçin (${langCode})`}
              logoFileList={getCurrentFileList()}
              setLogoFileList={handleFileChange}
              field="img"
              setFieldValue={() => {}}
            />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="url">
              Instagram URL (Ümumi)
            </label>
            <div className="input_wrapper">
              <input
                id="url"
                name="url"
                placeholder="https://instagram.com/..."
                value={values.url}
                onChange={(e) => setFieldValue("url", e.target.value)}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.url} touched={touched.url} />
          </div>

          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Yadda saxla" : "Əlavə et"}
            </MainButton>
          </div>
        </form>
      </Modal>

      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default InstagramGallery;
